/**
 * Generic Klaviyo Back-in-Stock handler.
 * Given a container element with standard `data-js` hooks, handles form submission
 * to Klaviyo's BIS endpoint and optionally subscribes to a mailing list.
 * When the text-message opt-in is ticked, the shopper is also given SMS
 * marketing consent on the SMS list — Klaviyo only sends back-in-stock texts
 * to profiles with that consent.
 */
(function () {
  if (window.KlaviyoNotifyMe) return;

  const KLAVIYO_BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  const KLAVIYO_LIST_SUBSCRIBE_ENDPOINT = 'https://manage.kmail-lists.com/ajax/subscriptions/subscribe';
  const KLAVIYO_CLIENT_SUBSCRIPTION_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  const KLAVIYO_API_REVISION = '2026-07-15';
  const NOTIFY_SUCCESS_DISMISS_MS = 2400;
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // The countries Klaviyo can collect SMS consent for and text.
  const SMS_DIAL_CODES = {
    AT: '43', AU: '61', BE: '32', CA: '1', CH: '41', DE: '49', DK: '45', ES: '34', FI: '358', FR: '33', GB: '44',
    HU: '36', IE: '353', IT: '39', LU: '352', NL: '31', NO: '47', NZ: '64', PL: '48', PT: '351', SE: '46', US: '1',
  };

  /**
   * Flag emoji for an ISO 3166-1 alpha-2 code, built from regional indicator symbols.
   * @param {string} iso
   * @returns {string}
   */
  function flagEmoji(iso) {
    return String.fromCodePoint(...Array.from(iso.toUpperCase(), (letter) => 0x1f1e6 + letter.charCodeAt(0) - 65));
  }

  /**
   * Normalise a typed mobile number to E.164. Numbers without a `+` or `00`
   * prefix are read as local to `dialCode`, dropping the national trunk `0`.
   * @param {string} raw - The number as typed.
   * @param {string} dialCode - Country calling code without the `+`, e.g. `61`.
   * @returns {string|null} The E.164 number, or null when it cannot be resolved.
   */
  function toE164(raw, dialCode) {
    const digits = raw.replace(/[\s().-]/g, '');
    let international = null;

    if (digits.startsWith('+')) {
      international = digits.slice(1);
    } else if (digits.startsWith('00')) {
      international = digits.slice(2);
    } else {
      if (!dialCode) return null;
      international = digits.startsWith(dialCode) ? digits : dialCode + digits.replace(/^0/, '');
    }

    return /^[1-9]\d{7,14}$/.test(international) ? '+' + international : null;
  }

  class KlaviyoNotifyMe {
    /**
     * @param {HTMLElement} panel - The main `data-js="notify-me"` container
     * @param {Object} [options]
     * @param {Function} [options.onResize] - Called when the form state changes (e.g. error shown)
     * @param {Function} [options.onSuccess] - Called after a successful submission, before auto-close
     * @param {Function} [options.onClose] - Called to trigger the panel's close animation
     */
    constructor(panel, options = {}) {
      this.panel = panel;
      this.options = options;

      this.form = panel.querySelector('[data-js="notify-me-form"]');
      this.variantInput = panel.querySelector('[data-js="notify-me-variant-id"]');
      this.emailInput = panel.querySelector('[data-js="notify-me-email"]');
      this.marketingCheck = panel.querySelector('[data-js="notify-me-marketing"]');
      this.smsCheck = panel.querySelector('[data-js="notify-me-sms"]');
      this.smsFields = panel.querySelector('[data-js="notify-me-sms-fields"]');
      this.phoneInput = panel.querySelector('[data-js="notify-me-phone"]');
      this.dialSelect = panel.querySelector('[data-js="notify-me-dial-code"]');
      this.dialDisplay = panel.querySelector('[data-js="notify-me-dial-display"]');
      this.successText = panel.querySelector('[data-js="notify-me-success-text"]');
      this.moreEl = panel.querySelector('[data-js="notify-me-more"]');
      this.moreSizesEl = panel.querySelector('[data-js="notify-me-more-sizes"]');
      this.moreErrorEl = panel.querySelector('[data-js="notify-me-more-error"]');
      this.moreTemplate = panel.querySelector('[data-js="notify-me-more-size-template"]');
      this.errorEl = panel.querySelector('[data-js="notify-me-error"]');
      this.successEl = panel.querySelector('[data-js="notify-me-success"]');
      this.submitBtn = panel.querySelector('[data-js="notify-me-submit"]');
      this.submitLabel = panel.querySelector('[data-js="notify-me-submit-label"]');
      this.submitSpinner = panel.querySelector('[data-js="notify-me-submit-spinner"]');

      this.successTimer = null;
      this.emailPlaceholder = this.emailInput ? this.emailInput.placeholder : '';
      this.otherSizes = [];
      this.lastContact = null;

      this.handleSubmit = this.handleSubmit.bind(this);
      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit);
      }
      if (this.smsCheck) {
        this.smsCheck.addEventListener('change', () => this.toggleSms());
      }
      if (this.moreSizesEl) {
        this.moreSizesEl.addEventListener('click', (event) => this.handleMoreSizeClick(event));
      }
      if (this.dialSelect) {
        this.populateDialCodes();
        this.dialSelect.addEventListener('change', () => this.syncDialDisplay());
      }
    }

    /**
     * Fill the country-code picker with the SMS countries, named in the page
     * language and sorted by name, selecting the shopper's market country.
     * @returns {void}
     */
    populateDialCodes() {
      const lang = document.documentElement.lang || 'en';
      let regionNames = null;
      try {
        regionNames = new Intl.DisplayNames([lang], { type: 'region' });
      } catch (_err) {
        regionNames = null;
      }

      const defaultCountry = (this.dialSelect.dataset.defaultCountry || '').toUpperCase();
      const entries = Object.keys(SMS_DIAL_CODES)
        .map((iso) => ({ iso, dial: SMS_DIAL_CODES[iso], name: (regionNames && regionNames.of(iso)) || iso }))
        .sort((a, b) => a.name.localeCompare(b.name, lang));

      const options = entries.map((entry) => {
        const option = document.createElement('option');
        option.value = entry.iso;
        option.dataset.dial = entry.dial;
        option.textContent = flagEmoji(entry.iso) + ' ' + entry.name + ' (+' + entry.dial + ')';
        option.defaultSelected = entry.iso === defaultCountry;
        return option;
      });

      if (!SMS_DIAL_CODES[defaultCountry]) {
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = this.dialSelect.dataset.placeholder || '';
        placeholder.defaultSelected = true;
        options.unshift(placeholder);
      }

      this.dialSelect.replaceChildren(...options);
      this.syncDialDisplay();
    }

    /**
     * The selected country's calling code, without the `+`.
     * @returns {string}
     */
    selectedDialCode() {
      const option = this.dialSelect && this.dialSelect.selectedOptions[0];
      return option ? option.dataset.dial || '' : '';
    }

    /**
     * Show the selected country's flag in the closed picker, or the
     * placeholder when no country is selected.
     * @returns {void}
     */
    syncDialDisplay() {
      if (!this.dialDisplay || !this.dialSelect) return;
      const iso = this.dialSelect.value;
      this.dialDisplay.textContent = iso ? flagEmoji(iso) : this.dialSelect.dataset.placeholder || '';
      this.dialDisplay.dataset.empty = iso ? 'false' : 'true';
    }

    /**
     * Reveal or hide the mobile number field to match the text-message opt-in.
     * @returns {void}
     */
    toggleSms() {
      if (!this.smsFields || !this.phoneInput) return;
      const wantsSms = Boolean(this.smsCheck && this.smsCheck.checked);
      this.smsFields.hidden = !wantsSms;
      this.phoneInput.required = wantsSms;
      this.syncEmailRequirement(wantsSms);
      if (wantsSms) this.phoneInput.focus();
      if (this.options.onResize) this.options.onResize();
    }

    /**
     * Email is optional while the text opt-in is on, since a mobile number
     * alone is enough to subscribe.
     * @param {boolean} wantsSms
     * @returns {void}
     */
    syncEmailRequirement(wantsSms) {
      if (!this.emailInput) return;
      this.emailInput.required = !wantsSms;
      this.emailInput.placeholder = wantsSms
        ? this.emailInput.dataset.placeholderOptional || this.emailPlaceholder
        : this.emailPlaceholder;
    }

    /**
     * Lock or unlock the form while a submission is in flight.
     * @param {boolean} submitting
     * @returns {void}
     */
    setSubmitting(submitting) {
      this.submitBtn.disabled = submitting;
      this.emailInput.disabled = submitting;
      if (this.phoneInput) this.phoneInput.disabled = submitting;
      if (this.dialSelect) this.dialSelect.disabled = submitting;
      if (this.submitLabel) this.submitLabel.hidden = submitting;
      if (this.submitSpinner) this.submitSpinner.hidden = !submitting;
    }

    /**
     * Reset the form to its initial state.
     * @param {Object} [options]
     * @param {boolean} [options.keepContact] - Keep the email, mobile and
     *   opt-in choices, for a shopper moving on to another size.
     * @returns {void}
     */
    reset({ keepContact = false } = {}) {
      if (this.successTimer) {
        clearTimeout(this.successTimer);
        this.successTimer = null;
      }
      if (this.form) {
        this.form.hidden = false;
        delete this.form.dataset.state;
      }
      if (!keepContact) {
        if (this.emailInput) this.emailInput.value = '';
        if (this.marketingCheck) this.marketingCheck.checked = false;
        if (this.smsCheck) this.smsCheck.checked = false;
        if (this.smsFields) this.smsFields.hidden = true;
        this.syncEmailRequirement(false);
        if (this.phoneInput) {
          this.phoneInput.value = '';
          this.phoneInput.required = false;
        }
        if (this.dialSelect) {
          Array.from(this.dialSelect.options).forEach((option) => {
            option.selected = option.defaultSelected;
          });
          this.syncDialDisplay();
        }
      }
      if (this.emailInput) this.emailInput.disabled = false;
      if (this.phoneInput) this.phoneInput.disabled = false;
      if (this.dialSelect) this.dialSelect.disabled = false;
      if (this.successText) {
        this.successText.textContent = this.successText.dataset.textEmail || this.successText.textContent;
      }
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
      if (this.successEl) {
        this.successEl.hidden = true;
        delete this.successEl.dataset.state;
      }
      if (this.submitBtn) {
        this.submitBtn.disabled = false;
      }
      if (this.submitLabel) {
        this.submitLabel.hidden = false;
      }
      if (this.submitSpinner) {
        this.submitSpinner.hidden = true;
      }
      this.otherSizes = [];
      this.lastContact = null;
      if (this.moreEl) this.moreEl.hidden = true;
      if (this.moreSizesEl) this.moreSizesEl.replaceChildren();
      if (this.moreErrorEl) this.moreErrorEl.hidden = true;
    }

    /**
     * Sizes offered as one-tap extra subscriptions once the form succeeds.
     * @param {Array<{id: string, label: string}>} sizes - Other sold-out
     *   variants of the same product, excluding the one in the form.
     * @returns {void}
     */
    setOtherSizes(sizes) {
      this.otherSizes = Array.isArray(sizes) ? sizes.filter((size) => size && size.id) : [];
    }

    /**
     * Render the other-sizes buttons into the success state.
     * @returns {boolean} Whether any were shown.
     */
    renderOtherSizes() {
      if (!this.moreEl || !this.moreSizesEl || !this.moreTemplate || !this.otherSizes.length) return false;
      const buttons = this.otherSizes.map((size) => {
        const fragment = this.moreTemplate.content.cloneNode(true);
        const button = fragment.querySelector('[data-js="notify-me-more-size"]');
        const label = fragment.querySelector('[data-js="notify-me-more-size-label"]');
        button.dataset.variantId = String(size.id);
        if (label) label.textContent = size.label;
        return fragment;
      });
      this.moreSizesEl.replaceChildren(...buttons);
      if (this.moreErrorEl) this.moreErrorEl.hidden = true;
      this.moreEl.hidden = false;
      return true;
    }

    /**
     * Subscribe the same contact details to another size.
     * @param {MouseEvent} event
     * @returns {Promise<void>}
     */
    async handleMoreSizeClick(event) {
      const button = event.target.closest('[data-js="notify-me-more-size"]');
      if (!button || button.dataset.state || !this.lastContact) return;

      button.dataset.state = 'sending';
      button.disabled = true;
      if (this.moreErrorEl) this.moreErrorEl.hidden = true;

      const { companyId, profileAttributes, channels } = this.lastContact;
      const status = await this.subscribeToBackInStock(companyId, button.dataset.variantId, profileAttributes, channels);

      if (status === 202) {
        button.dataset.state = 'sent';
        button.setAttribute('aria-pressed', 'true');
        return;
      }
      delete button.dataset.state;
      button.disabled = false;
      if (this.moreErrorEl) this.moreErrorEl.hidden = false;
      if (this.options.onResize) this.options.onResize();
    }

    async handleSubmit(e) {
      e.preventDefault();
      if (!this.emailInput || !this.variantInput) return;

      const email = (this.emailInput.value || '').trim();
      const variantId = String(this.variantInput.value || '').trim();
      const companyId = this.panel.dataset.klaviyoId;
      const listId = this.panel.dataset.klaviyoList || '';
      const smsListId = this.panel.dataset.klaviyoSmsList || '';
      const wantsSms = Boolean(this.smsCheck && this.smsCheck.checked && this.phoneInput && smsListId);
      const phoneRaw = wantsSms ? (this.phoneInput.value || '').trim() : '';

      if (!variantId || !companyId) {
        this.showError('submit_failed');
        return;
      }
      if (email && !EMAIL_PATTERN.test(email)) {
        this.showError('missing_fields');
        return;
      }
      if (!email && !phoneRaw) {
        this.showError(wantsSms ? 'missing_contact' : 'missing_fields');
        return;
      }

      const phone = phoneRaw ? toE164(phoneRaw, this.selectedDialCode()) : null;
      if (phoneRaw && !phone) {
        this.showError('invalid_phone');
        return;
      }

      const profileAttributes = {};
      if (email) profileAttributes.email = email;
      if (phone) profileAttributes.phone_number = phone;
      const channels = [email && 'EMAIL', phone && 'SMS'].filter(Boolean);

      this.setSubmitting(true);
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
        if (this.options.onResize) this.options.onResize();
      }

      if (phone) {
        const smsStatus = await this.subscribeToSms(companyId, smsListId, email, phone);
        if (smsStatus !== 202) {
          this.showError(smsStatus === 400 ? 'invalid_phone' : 'submit_failed');
          this.setSubmitting(false);
          return;
        }
      }

      const bisStatus = await this.subscribeToBackInStock(companyId, variantId, profileAttributes, channels);
      if (bisStatus !== 202) {
        this.showError('submit_failed');
        this.setSubmitting(false);
        return;
      }

      this.lastContact = { companyId, profileAttributes, channels };

      if (email && this.marketingCheck && this.marketingCheck.checked && listId) {
        // Fire-and-forget list subscription
        this.subscribeToList(listId, email).catch(function () {});
      }

      if (phone && this.successText) {
        const smsText = email ? this.successText.dataset.textSms : this.successText.dataset.textSmsOnly;
        if (smsText) this.successText.textContent = smsText;
      }
      this.showSuccess();
    }

    /**
     * Register a back-in-stock subscription for one variant.
     * @param {string} companyId - Klaviyo public API key.
     * @param {string} variantId - Shopify variant id.
     * @param {Object} profileAttributes - `email` and/or `phone_number`.
     * @param {Array<string>} channels - `EMAIL` and/or `SMS`.
     * @returns {Promise<number>} The response status, or 0 on a network failure.
     */
    async subscribeToBackInStock(companyId, variantId, profileAttributes, channels) {
      const payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: profileAttributes,
              },
            },
            channels: channels,
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: '$shopify:::$default:::' + variantId,
              },
            },
          },
        },
      };

      try {
        const res = await fetch(KLAVIYO_BIS_ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: KLAVIYO_API_REVISION,
          },
          body: JSON.stringify(payload),
        });
        return res.status;
      } catch (_err) {
        return 0;
      }
    }

    showSuccess() {
      const offersMoreSizes = this.renderOtherSizes();
      if (this.successEl) {
        this.successEl.hidden = false;
        requestAnimationFrame(() => {
          this.successEl.dataset.state = 'visible';
        });
      }
      if (this.form) this.form.dataset.state = 'hidden';

      if (this.options.onSuccess) {
        this.options.onSuccess();
      }

      setTimeout(() => {
        if (this.form) this.form.hidden = true;
        if (this.options.onResize) this.options.onResize();
      }, 320);

      if (offersMoreSizes) return;
      this.successTimer = setTimeout(() => {
        this.successTimer = null;
        if (this.options.onClose) this.options.onClose();
      }, NOTIFY_SUCCESS_DISMISS_MS);
    }

    showError(key) {
      if (!this.errorEl) return;
      const messages = {
        missing_fields: this.errorEl.dataset.errorInvalidEmail || 'Please enter a valid email address.',
        invalid_phone: this.errorEl.dataset.errorInvalidPhone || 'Please enter a valid mobile number.',
        missing_contact: this.errorEl.dataset.errorMissingContact || 'Please enter your email or mobile number.',
        submit_failed: this.errorEl.dataset.errorGeneric || 'Something went wrong. Please try again.',
      };
      this.errorEl.textContent = messages[key] || messages.submit_failed;
      this.errorEl.hidden = false;
      if (this.options.onResize) this.options.onResize();
    }

    async subscribeToList(listId, email) {
      const body = new URLSearchParams();
      body.set('g', listId);
      body.set('email', email);
      await fetch(KLAVIYO_LIST_SUBSCRIBE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Cache-Control': 'no-cache',
        },
        body: body.toString(),
      });
    }

    /**
     * Give the profile SMS marketing consent on the SMS list. Klaviyo answers
     * 400 for numbers it cannot text (invalid, or a country the sending number
     * does not cover).
     * @param {string} companyId - Klaviyo public API key.
     * @param {string} listId - Klaviyo SMS list id.
     * @param {string} email - May be empty when the shopper only gave a mobile number.
     * @param {string} phone - E.164 mobile number.
     * @returns {Promise<number>} The response status, or 0 on a network failure.
     */
    async subscribeToSms(companyId, listId, email, phone) {
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Back in stock',
            profile: {
              data: {
                type: 'profile',
                attributes: Object.assign(email ? { email: email } : {}, {
                  phone_number: phone,
                  subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } },
                }),
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: listId } },
          },
        },
      };

      try {
        const res = await fetch(
          KLAVIYO_CLIENT_SUBSCRIPTION_ENDPOINT + '?company_id=' + encodeURIComponent(companyId),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: KLAVIYO_API_REVISION,
            },
            body: JSON.stringify(payload),
          }
        );
        return res.status;
      } catch (_err) {
        return 0;
      }
    }
  }

  window.KlaviyoNotifyMe = KlaviyoNotifyMe;
})();
